import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { announcementService, getNotificationsEnabled } from '@moal/api'
import { AnnouncementInputModel, AnnouncementViewModel } from '@moal/types'
import { AxiosError } from 'axios'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { PRODUCT_CODE } from '../../../utils/ENV'
import { postAnnouncementValidation } from '../../../utils/postAnnouncementHelpers'
import { toastSuccess } from '../../../utils/toast'
import { sanitizeText } from '../../../utils/utilities'
import ReactQuillEditor from '../../Utilities/ReactQuillEditor'
import Tooltip from '../../Utilities/Tooltip'
import SendNotifications from '../common/SendNotifications'

const ANNOUNCEMENT_FIELDS_MAX_LENGTH = {
  TITLE: 80,
  SUBJECT: 80,
  MESSAGE: 2000,
  CTA_TEXT: 36
}

interface PostAnnouncementFormProps {
  edit: boolean
  closeModal: Dispatch<SetStateAction<boolean>>
  prefill: AnnouncementViewModel
}

interface AnnouncementParams {
  _id?: string
  announcement: AnnouncementInputModel
  sendNotifications: boolean
  subjectLine: string
}

const defaultParams: AnnouncementParams = {
  announcement: {
    title: '',
    message: '',
    cta: {
      url: '',
      text: ''
    },
    itemNumber: PRODUCT_CODE
  },
  sendNotifications: true,
  subjectLine: ''
}

const PostAnnouncementForm = ({
  edit,
  closeModal,
  prefill
}: PostAnnouncementFormProps) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState({
    state: false,
    message: ''
  })
  const [notificationsEnabled, setNotificationsEnabled] = useState(false)

  const [announcementParams, setAnnouncementParams] = useState(defaultParams)
  const [limitsState, setLimitsState] = useState({
    title: {
      limit: ANNOUNCEMENT_FIELDS_MAX_LENGTH.TITLE,
      current: 0,
      exceeded: false
    },
    message: {
      limit: ANNOUNCEMENT_FIELDS_MAX_LENGTH.MESSAGE,
      current: 0,
      exceeded: false
    },
    ctaText: {
      limit: ANNOUNCEMENT_FIELDS_MAX_LENGTH.CTA_TEXT,
      current: 0,
      exceeded: false
    },
    subjectLine: {
      limit: ANNOUNCEMENT_FIELDS_MAX_LENGTH.SUBJECT,
      current: 0,
      exceeded: false
    }
  })

  const [keyModifer, setKeyModifier] = useState(0)

  useEffect(() => {
    const storageAnnouncementParams = localStorage.getItem(
      'temp_announcement_params'
    )
    if (storageAnnouncementParams) {
      const tempAnnouncementParams = JSON.parse(storageAnnouncementParams)
      setAnnouncementParams({
        announcement: {
          title:
            tempAnnouncementParams.announcement.title ??
            defaultParams.announcement.title,
          message:
            tempAnnouncementParams.announcement.message ??
            defaultParams.announcement.message,
          cta: {
            url:
              tempAnnouncementParams.announcement.cta?.url ??
              defaultParams.announcement.cta?.url,
            text:
              tempAnnouncementParams.announcement.cta?.text ??
              defaultParams.announcement.cta?.text
          },
          itemNumber: PRODUCT_CODE
        },
        subjectLine:
          tempAnnouncementParams.subjectLine ?? defaultParams.subjectLine,
        sendNotifications:
          tempAnnouncementParams.sendNotifications ??
          defaultParams.sendNotifications
      })
      setKeyModifier(keyModifer + 1)
    }
    fetchNotificationsEnabled()
  }, [])

  useEffect(() => {
    localStorage.setItem(
      'temp_announcement_params',
      JSON.stringify(announcementParams)
    )
  }, [announcementParams])

  useEffect(() => {
    if (prefill) {
      setAnnouncementParams({
        ...defaultParams,
        _id: prefill._id,
        announcement: {
          title: prefill.title,
          message: prefill.message,
          cta: {
            url: prefill.cta?.url,
            text: prefill.cta?.text
          },
          itemNumber: prefill.itemNumber
        }
      })
      setKeyModifier(keyModifer + 1)
    }
  }, [prefill])

  useEffect(() => {
    // get just the plain text from message
    const throwaway = document.createElement('div')
    throwaway.innerHTML = announcementParams.announcement.message
    const plainTextMessage = throwaway.innerText
    throwaway.remove()
    // Check if any fields exceed the limit
    setLimitsState((old) => ({
      ...old,
      title: {
        ...old.title,
        current: announcementParams.announcement.title.length,
        exceeded: announcementParams.announcement.title.length > old.title.limit
      },
      message: {
        ...old.message,
        current: plainTextMessage.length,
        exceeded: plainTextMessage.length > old.message.limit
      },
      subjectLine: {
        ...old.subjectLine,
        current: announcementParams.subjectLine.length,
        exceeded: announcementParams.subjectLine.length > old.subjectLine.limit
      },
      ctaText: {
        ...old.ctaText,
        current: announcementParams.announcement.cta?.text?.length,
        exceeded:
          announcementParams.announcement.cta?.text?.length > old.ctaText.limit
      }
    }))
  }, [announcementParams])

  const fetchNotificationsEnabled = async () => {
    const response = await getNotificationsEnabled(PRODUCT_CODE)
    if (response.status === 200 && response.data.notificationsEnabled) {
      setNotificationsEnabled(true)
    } else {
      setAnnouncementParams((old) => ({ ...old, sendNotifications: false }))
    }
  }

  const handleSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault()
    setLoading(true)
    setError({
      state: false,
      message: ''
    })

    try {
      // Validation
      const { error: validationError, message } = postAnnouncementValidation(
        limitsState,
        announcementParams.announcement.cta
      )
      if (validationError) {
        throw new Error(message)
      }

      if (
        !announcementParams.announcement.cta.url ||
        (announcementParams.announcement.cta.text === '' &&
          announcementParams.announcement.cta.url === '')
      ) {
        delete announcementParams.announcement.cta
      }
      announcementParams.announcement.message = sanitizeText(
        announcementParams.announcement.message
      )

      if (edit) {
        // Handle Edit here
        const id = announcementParams._id
        await announcementService.editById(id, announcementParams.announcement)
      } else {
        // Handle Post here
        await announcementService.create(
          announcementParams.announcement,
          announcementParams.sendNotifications,
          announcementParams.subjectLine
        )
      }

      toastSuccess(`Announcement ${edit ? 'updated' : 'posted'} successfully.`)
      handleResetForm()
      if (closeModal) {
        closeModal(false)
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        setError({
          state: true,
          message: edit
            ? 'Error updating announcement.'
            : 'Error posting announcement.'
        })
      } else {
        setError({
          state: true,
          message: error.message || 'An unexpected error occurred.'
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleResetForm = () => {
    setAnnouncementParams(defaultParams)
    localStorage.setItem(
      'temp_announcement_params',
      JSON.stringify(defaultParams)
    )
    setKeyModifier(keyModifer + 1)
  }

  return (
    <form
      className="w-full p-0 lg:p-5 lg:bg-neutral-50 lg:dark:bg-neutral-950 lg:rounded-xl"
      onSubmit={handleSubmit}
      autoComplete="off"
    >
      <div className="flex w-full flex-col gap-4">
        <div className="flex items-center">
          <h2 className="mr-3">Send a New Announcement</h2>
          <Tooltip>Send announcements to users via email.</Tooltip>
        </div>
        <div className="flex flex-col">
          <label className="dark:text-neutral-100">
            Title <span className="text-red-500">*</span>
            <span className="ml-2 text-xs">
              <span
                className={limitsState.title.exceeded ? 'text-red-500' : ''}
              >
                {limitsState.title.current} / {limitsState.title.limit}
              </span>
            </span>
          </label>
          <input
            value={announcementParams.announcement.title}
            placeholder="Announcement Title"
            onChange={(e) =>
              setAnnouncementParams((old) => ({
                ...old,
                announcement: {
                  ...old.announcement,
                  title: e.target.value
                }
              }))
            }
            className="input"
            required
            maxLength={limitsState.title.limit}
          />
        </div>
        <div className="flex flex-col">
          <label className="dark:text-neutral-100">
            Subject Line <span className="font-light text-xs">(Optional)</span>
            <span className="ml-2 text-xs">
              <span
                className={
                  limitsState.subjectLine.exceeded ? 'text-red-500' : ''
                }
              >
                {limitsState.subjectLine.current} /{' '}
                {limitsState.subjectLine.limit}
              </span>
            </span>
          </label>
          <input
            value={announcementParams.subjectLine}
            placeholder="Subject Line"
            onChange={(e) =>
              setAnnouncementParams((old) => ({
                ...old,
                subjectLine: e.target.value
              }))
            }
            className="input"
            maxLength={limitsState.subjectLine.limit}
          />
        </div>
        <div className="flex flex-col">
          <label className="dark:text-neutral-100">
            Message <span className="text-red-500">*</span>
            <span className="ml-2 text-xs">
              <span
                className={limitsState.message.exceeded ? 'text-red-500' : ''}
              >
                {limitsState.message.current} / {limitsState.message.limit}{' '}
                {limitsState.message.exceeded
                  ? 'Your message exceeds the limit.'
                  : ''}
              </span>
            </span>
          </label>
          <div className="bg-white dark:bg-neutral-800">
            <ReactQuillEditor
              key={keyModifer}
              initialHtml={announcementParams.announcement.message}
              onChange={(html) => {
                setAnnouncementParams((old) => ({
                  ...old,
                  announcement: {
                    ...old.announcement,
                    message: html
                  }
                }))
              }}
              maxLength={ANNOUNCEMENT_FIELDS_MAX_LENGTH.MESSAGE}
            />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex flex-col w-full">
            <label className="dark:text-neutral-100">Call To Action URL</label>
            <input
              value={announcementParams.announcement.cta?.url ?? ''}
              placeholder="Call To Action URL"
              type="url"
              onChange={(e) =>
                setAnnouncementParams((old) => ({
                  ...old,
                  announcement: {
                    ...old.announcement,
                    cta: {
                      ...old.announcement.cta,
                      url: e.target.value
                    }
                  }
                }))
              }
              className="input"
            />
          </div>
          <div className="flex flex-col w-full">
            <label className="dark:text-neutral-100">
              Call To Action Text{' '}
              {announcementParams.announcement.cta?.url?.length > 0 && (
                <span className="text-red-500">*</span>
              )}
              <span className="ml-2 text-xs">
                <span
                  className={limitsState.ctaText.exceeded ? 'text-red-500' : ''}
                >
                  {limitsState.ctaText.current} / {limitsState.ctaText.limit}
                </span>
              </span>
            </label>
            <input
              value={announcementParams.announcement.cta?.text ?? ''}
              placeholder="Call To Action Text"
              onChange={(e) =>
                setAnnouncementParams((old) => ({
                  ...old,
                  announcement: {
                    ...old.announcement,
                    cta: {
                      ...old.announcement.cta,
                      text: e.target.value
                    }
                  }
                }))
              }
              className="input"
              maxLength={limitsState.ctaText.limit}
              disabled={!(announcementParams.announcement.cta?.url?.length > 0)}
              required={announcementParams.announcement.cta?.url?.length > 0}
            />
          </div>
        </div>
        {notificationsEnabled && (
          <SendNotifications
            checked={announcementParams.sendNotifications}
            handleChange={(e) =>
              setAnnouncementParams((old) => ({
                ...old,
                sendNotifications: e.target.checked
              }))
            }
          />
        )}

        {error.state && (
          <div className="w-fit p-2 rounded border border-orange-500 bg-orange-200 dark:bg-orange-900">
            <p>
              <FontAwesomeIcon icon={faExclamationTriangle} /> {error.message}
            </p>
          </div>
        )}
        <div className="flex justify-between">
          <input
            type="submit"
            className="button-confirm w-fit"
            value={loading ? 'Submitting...' : 'Submit'}
          />
          <button className="button-cancel w-fit" onClick={handleResetForm}>
            Reset Form
          </button>
        </div>
      </div>
    </form>
  )
}

export default PostAnnouncementForm
